<template>
    <div
        ref="selectContainer"
        class="result-select-container"
        @click.stop="toggleDropdown"
    >
        <div class="result-select__wrapper">
            <label
                v-if="label"
                :for="id"
                class="result-select__label"
            >
                {{ label }}
            </label>

            <div
                :class="[
                    'result-select-field',
                    {
                        'result-select-field_open': isOpen,
                        'has-value': modelValue,
                    },
                ]"
            >
                <span :class="{ 'has-value': modelValue }">
                    {{ selectedOptionLabel }}
                </span>

                <img
                    v-svg-inline
                    src="@/assets/icons/ui/select-arrow.svg"
                    class="result-select-field__arrow"
                    :class="{ 'result-select-field__arrow_open': isOpen }"
                />
            </div>
        </div>

        <div v-if="isOpen" class="result-select-field__dropdown">
            <div
                v-for="option in options"
                :key="option.value"
                class="result-select-field__option"
                :class="{ selected: option.value === modelValue }"
                @click.stop="selectOption(option)"
            >
                {{ option.label }}
            </div>
        </div>

        <div class="result-select-field--mobile" @click="openSortModal">
            <img
                v-svg-inline
                src="@/assets/icons/ui/mi_sort.svg"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, computed, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue';
    import type { PropType } from 'vue';

    import { injectStrict } from '@/injection/injectStrict.ts';
    import { ModalPluginType } from '@/plugins/modalPlugin.ts';
    import { ModalKey } from '@/injection/keys.ts';

    import { SortOption } from '@/types/form.type.ts';

    import SortModal from '@/modals/SortModal/SortModal.vue';

    const props = defineProps({
        modelValue: {
            type: [String, Number] as PropType<string | undefined>,
            default: undefined,
        },
        options: {
            type: Array as PropType<SortOption[]>,
            default: () => [],
        },
        label: {
            type: String,
            default: '',
        },
        id: {
            type: String,
            default: '',
        },
    });

    const emit = defineEmits<{
        (e: 'update:modelValue', value: string): void,
    }>();

    const $modal = injectStrict<ModalPluginType>(ModalKey);

    const isOpen = ref(false);
    const selectContainer = ref<HTMLElement | null>(null);

    const selectedOptionLabel = computed(() => {
        const selectedOption = props.options.find((option) => option.value === props.modelValue);

        return selectedOption ? selectedOption.label : 'Выберите значение';
    });

    function toggleDropdown(): void {
        isOpen.value = !isOpen.value;
    }

    function openSortModal(): void {
        $modal.open({
            component: SortModal,
            props: {
                selectedOption: props.modelValue,
                options: props.options,
                onReset: () => {
                    emit('update:modelValue', undefined);
                    $modal.close();
                },
                onChange: (newValue: string) => {
                    emit('update:modelValue', newValue);
                    $modal.close();
                },
            },
        });
    }

    function selectOption(option: SortOption): void {
        emit('update:modelValue', option.value);
        isOpen.value = false;
    }

    function handleClickOutside(event: MouseEvent): void {
        if (selectContainer.value && !selectContainer.value.contains(event.target as Node)) {
            isOpen.value = false;
        }
    }

    onMounted(() => {
        window.addEventListener('click', handleClickOutside);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('click', handleClickOutside);
    });
</script>

<style lang="scss" scoped>
    @use './UIResultSelect.scss' as *;
</style>
